import { FlatList, Pressable, SxProp, View } from 'dripsy';
import { t } from 'i18next';
import React, { useCallback } from 'react';
import { CurrenciesIcon } from '@/features/earn/components';
import { keyGenerator } from '@/utils/keyGenerator';
import { formatAmountByDecimals } from '@/utils/numberFormatting';
import { useFocusEffect } from '@react-navigation/native';
import { Platform } from 'react-native';
import { Icon } from '@/design-system/components/icons/Icon';
import { UniInput } from '@/design-system/components/inputs';
import { useNativeModalView } from '@/design-system/components/modal/nativeModal/useNativeModalView';
import { TextBody, TextSmall, TextTitle } from '@/design-system/components/typography';
import { useAccountSelectorNative, WalletsByAccountLabelItem } from '@/design-system/components/accountSelector/useAccountSelectorNative';
import { PageHeaderModalNative } from '@/features/bill-payment/screens/components/PageHeaderModalNative';
import { UniIconButton } from '@/design-system/components/buttons';

export interface AccountSelectorNativeModalProps {
  asset?: any;
  onSelect: (walletId: string, wallet?: any) => void;
  value?: string;
  disabledForwardBrowser?: boolean;
}

export const AccountSelectorNativeModal: React.FC<AccountSelectorNativeModalProps> = (props) => {
  const { asset, value, disabledForwardBrowser = false, onSelect } = props;

  const { handleBack, animatedStyle } = useNativeModalView({});

  const { displayWallets, walletsSortByAccountLabel, handleChangeSearchText } = useAccountSelectorNative({ asset });

  const isWeb = Platform.OS === 'web';

  const onSelectWalletId = (walletId: string, wallet?: any) => {
    onSelect(walletId, wallet);
    handleBack();
  };

  // Disable forward browser button
  useFocusEffect(
    useCallback(() => {
      if (isWeb && disabledForwardBrowser) {
        const onPopState = (event: any) => {
          window.history.pushState(event.state, '');
        };
        window.addEventListener('popstate', onPopState);
        return () => window.removeEventListener('popstate', onPopState);
      }
    }, [])
  );

  const displayWalletsList = displayWallets.sort(
    (a, b) => Number(a.accountNumber.replace(/-/g, '')) - Number(b.accountNumber.replace(/-/g, ''))
  );

  return (
    <View sx={{ backgroundColor: '$backgroundPrimary', width: '100%', height: '100%' }}>
      <PageHeaderModalNative>
        <View sx={{ px: '$4', my: '$5' }}>
          <View sx={{ position: 'absolute', top: 0, left: 12, zIndex: 2 }}>
            <UniIconButton icon="arrowLeft" options={{ iconStyles: { themeColor: '$white' } }} onPress={() => handleBack()}></UniIconButton>
          </View>
          <View sx={{ textAlign: 'center', width: '100%' }}>
            <TextTitle bold sx={{ color: '$white' }}>
              {t('earn-invest::glossary.select-asset')}
            </TextTitle>
          </View>
          <View sx={{ position: 'absolute', top: 0, right: 12, zIndex: 2 }}></View>
        </View>
      </PageHeaderModalNative>
      <View sx={{ paddingX: '$4', paddingY: '$3' }}>
        <UniInput
          prefixIcon="search"
          wrapperSx={{ flex: 1 }}
          sx={{ width: '100%' }}
          onValueChange={(value) => handleChangeSearchText(value || '')}
          placeholder={t('unionPay::payment-instruction.glossary.search-by-asset')}
        />
      </View>
      <View sx={{ height: isWeb ? 'auto' : '80%' }}>
        {displayWallets.length > 0 && displayWalletsList.length > 0 ? (
          <FlatList
            data={displayWalletsList}
            keyExtractor={(item) => keyGenerator('accountSelector')}
            renderItem={({ item, index }: { item: WalletsByAccountLabelItem; index: number }) => (
              <React.Fragment>
                {(index == 0 || displayWalletsList[index - 1]?.accountNumber !== item.accountNumber) && (
                  <View sx={{ paddingX: '$4', pt: '$3', pb: '$1' }}>
                    <TextSmall semiBold sx={{ color: '$secondary' }}>
                      {item.accountLabel ? t(`earn-invest::glossary.${item.accountLabel}`) : item.accountName || ' - '}
                    </TextSmall>
                    <TextSmall sx={{ color: '$textSecondary' }}>{item.accountNumber}</TextSmall>
                  </View>
                )}
                <Pressable
                  sx={{
                    paddingX: '$4',
                    paddingY: '$2',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    backgroundColor: value === item.id ? '$disable' : 'transparent'
                  }}
                  onPress={() => onSelectWalletId(item.id, item)}>
                  <CurrenciesIcon asset={item.asset} textSize="textBody" textBold={false} />
                  <TextBody>{formatAmountByDecimals({ value: item.balance, decimals: item.decimals, suffix: item.asset })}</TextBody>
                </Pressable>
              </React.Fragment>
            )}
          />
        ) : (
          <View sx={{ flex: 1, height: '100%', paddingX: '$4', paddingY: '$3', justifyContent: 'center', alignItems: 'center' }}>
            <Icon name="listZeroState" height={60} width={60} />
            <TextBody bold sx={{ textAlign: 'center', marginTop: '$3' }}>
              {t('earn-invest::glossary.no-accounts-found')}
            </TextBody>
          </View>
        )}
      </View>
    </View>
  );
};

enum StyleName {
  container = 'container',
  content = 'content',
  header = 'header'
}

const styles: Record<StyleName, SxProp> = {
  container: {
    height: '100%',
    backgroundColor: '$backgroundPrimary'
  },
  content: {
    height: '100%',
    backgroundColor: '$backgroundPrimary'
  },
  header: {}
};
